import * as React from "react";
import styles from "./Search.module.scss";
import Feedback from "../../containers/Feedback/Feedback";
import magnifier from "../../assets/magnifier-white.svg";
import { domainComparator, getDomainDisplayName } from "../../utils/utils";
import LanguageSelection from "../LanguageSelection/LanguageSelection";
import { t } from "i18next";

type Props = {
  onSearchTermsChange: (searchTerms: SearchTerms) => void;
  domains: Api.Domain[];
  electrifications: Api.Electrification[];
  domainOptions: Api.Domain[];
  languageSelectionOpen: boolean;
  setLanguageSelectionOpen: (value: boolean) => void;
  currentDomainId?: number | null;
  currentElectrification?: number | null;
  currentFaultCode?: string;
  isFeedbackOpen: boolean;
  setFeedbackOpen: (value: boolean) => void;
};

type SearchState = {
  currentElectrification: number | null;
  currentDomainId: number | null;
  currentFaultCode?: string;
  currentAdditionalData?: string;
  isCodeValid: boolean;
};

class Search extends React.Component<Props, SearchState> {
  state = {
    currentElectrification: this.props.currentElectrification || -1,
    currentDomainId: this.props.currentDomainId || -1,
    currentFaultCode: this.props.currentFaultCode || "",
    currentAdditionalData: "",
    isCodeValid: false
  };

  componentDidMount(): void {
    this.setState(prevState => ({
      ...prevState,
      currentDomainId: this.props.currentDomainId || -1,
      currentFaultCode: this.props.currentFaultCode,
      isCodeValid: false
    }));
  }

  componentDidUpdate(prevProps: Props, prevState: SearchState): void {
    if (
      (this.props.currentFaultCode !== prevProps.currentFaultCode &&
        this.props.currentFaultCode != this.state.currentFaultCode) ||
      (this.props.currentDomainId !== prevProps.currentDomainId &&
        this.props.currentDomainId != this.state.currentDomainId) ||
      (this.props.currentElectrification !== prevProps.currentElectrification &&
        this.props.currentElectrification != this.state.currentElectrification)
    ) {
      this.setState({
        ...prevState,
        currentElectrification: this.props.currentElectrification || -1,
        currentDomainId: this.props.currentDomainId || -1,
        currentFaultCode: this.props.currentFaultCode,
        isCodeValid: false
      });
    }
  }
  render(): JSX.Element {
    const { electrifications, domains } = this.props;
    const electrificationSelector: any[] = [
      <option key={0} value={-1}>
        {t("all")}
      </option>
    ];
    electrificationSelector.push(
      electrifications.map(electrification => (
        <option key={electrification.enumValue} value={electrification.enumValue}>
          {electrification.enumName}
        </option>
      ))
    );

    const domainSelector: any[] = [
      <option key={0} value={-1}>
        {t("all")}
      </option>
    ];
    domainSelector.push(
      this.props.domainOptions.length > 0
        ? this.props.domainOptions.map((item: Api.Domain) => {
            const hasElectirifcation = item.electrifications?.find((e: any) => {
              return e.enumValue === Number(this.state.currentElectrification);
            });
            if (hasElectirifcation || Number(this.state.currentElectrification) === -1) {
              if (item.domainGroup.id !== 1) {
                return (
                  <option key={item.id} value={item.id}>
                    {getDomainDisplayName(item)}
                  </option>
                );
              }
            }
            return null;
          })
        : domains.sort(domainComparator(domains)).map((item: Api.Domain) => {
            const hasElectirifcation = item.electrifications?.find((e: any) => {
              return e.enumValue === Number(this.state.currentElectrification);
            });
            if (hasElectirifcation || Number(this.state.currentElectrification) === -1) {
              if (item.domainGroup.id !== 1) {
                return (
                  <option key={item.id} value={item.id}>
                    {getDomainDisplayName(item)}
                  </option>
                );
              }
            }
            return null;
          })
    );

    return (
      <>
        <div className={styles.Search}>
          <div className={styles.SearchDiv}>
            <label>
              {t("electrification")}
              <select
                value={this.state.currentElectrification}
                onChange={event => this.onElectrificationChange(event)}
              >
                {electrificationSelector}
              </select>
            </label>
          </div>
          <div className={styles.SearchDiv}>
            <label>
              {t("domain")}
              <select
                value={this.state.currentDomainId}
                onChange={event => this.onDomainChange(event)}
              >
                {domainSelector}
              </select>
            </label>
          </div>
          <div className={styles.SearchDiv}>
            <label>
              {t(`fault_code`)}
              {!this.state.isCodeValid ? "" : <span>(only numbers)</span>}
              <input
                value={this.state.currentFaultCode}
                onChange={event => this.onFaultCodeChange(event)}
                onKeyUp={event => this.onKeyUp(event)}
              />
              <input
                className={!this.state.isCodeValid ? "" : styles.disableImg}
                type="image"
                src={magnifier}
                onClick={() => this.onSubmitSearch()}
              />
            </label>
          </div>
          {this.props.isFeedbackOpen && (
            <div className={styles.FeedbackFormWrapper}>
              <Feedback close={() => this.props.setFeedbackOpen(false)} />{" "}
            </div>
          )}
          {this.props.languageSelectionOpen && (
            <div className={styles.FeedbackFormWrapper}>
              <LanguageSelection close={() => this.props.setLanguageSelectionOpen(false)} />{" "}
            </div>
          )}
        </div>
      </>
    );
  }
  onKeyUp(event: React.KeyboardEvent<HTMLInputElement>): void {
    if ((event.key === "Enter" || event.keyCode === 13) && !this.state.isCodeValid) {
      this.onSubmitSearch();
    }
  }
  onFaultCodeChange = (event: React.ChangeEvent<HTMLInputElement> | any): void => {
    const faultCodeValue = event.target.value;
    this.setState(prevState => ({
      ...prevState,
      currentFaultCode: faultCodeValue.trim().replace(".", ""),
      isCodeValid: isNaN(faultCodeValue)
    }));
  };
  onDomainChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const newDomain = Number(event.target.value);
    this.setState({ currentDomainId: newDomain }, () => this.onSubmitSearch());
  };
  onElectrificationChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const newElectrification = Number(event.target.value);
    this.setState({ currentElectrification: newElectrification, currentDomainId: -1 }, () =>
      this.onSubmitSearch()
    );
  };

  onSubmitSearch = (): void => {
    const searchTerms: SearchTerms = {
      searchCode: this.state.currentFaultCode,
      domainFilterInRequest: this.state.currentDomainId.toString(),
      electrification: this.state.currentElectrification.toString()
    };
    this.props.onSearchTermsChange(searchTerms);
  };
}

export default Search;
